import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../../../contexts/ToastContext';
import GestionPedidoBase from '../shared/GestionPedidoBase';
import API from '../../../../utils/api';
import './PedidoLlevar.css';

/**
 * Componente para crear/editar pedidos para llevar
 * Ahora usa GestionPedidoBase para la lógica centralizada
 */
const CreacionPedidoLlevar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const inicializacionCompleta = useRef(false);

  // ===== ESTADOS ESPECÍFICOS DE PEDIDOS PARA LLEVAR =====
  const [clienteNombre, setClienteNombre] = useState('');
  const [clienteTelefono, setClienteTelefono] = useState('');
  const [clienteCompleto, setClienteCompleto] = useState('');
  const [modalClienteVisible, setModalClienteVisible] = useState(false);
  const [clienteNombreTemp, setClienteNombreTemp] = useState('');
  const [clienteTelefonoTemp, setClienteTelefonoTemp] = useState('');
  
  // Estados del pedido específicos para llevar
  const [modoEdicion, setModoEdicion] = useState(false);
  const [pedidoExistente, setPedidoExistente] = useState(null);
  const [numeroCuenta, setNumeroCuenta] = useState('00');
  const [fechaHora, setFechaHora] = useState(new Date());

  // ===== EFECTOS =====

  // Fecha y hora inicial (sin actualización automática)
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setFechaHora(new Date());
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, []);

  // Cargar datos iniciales al montar
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // ===== FUNCIONES DE CARGA DE DATOS =====

  const cargarDatosIniciales = useCallback(async () => {
    if (inicializacionCompleta.current) return;

    try {
      // Verificar datos del estado de navegación (modo edición)
      const datosGuardados = location.state?.datosPedido;

      console.log('🔍 Datos recibidos en CreacionPedidoLlevar:', location.state);
      console.log('🔍 datosPedido extraído:', datosGuardados);

      if (datosGuardados) {
        const datos = datosGuardados;
        console.log('📋 Procesando datos del pedido:', datos);

        // Configurar modo edición si hay un pedido existente
        if (datos.esEdicion && datos.pedidoCreado) {
          console.log('🔧 Configurando modo edición con pedido:', datos.pedidoCreado);
          setModoEdicion(true);
          setNumeroCuenta(datos.pedidoCreado.numeroDia || '00');

          // Refrescar estado del pedido desde el servidor para tener datos actualizados
          let pedidoActualizado = datos.pedidoCreado;
          if (datos.pedidoCreado.idPedido) {
            try {
              const pedidoServer = await API.pedidos.getPedidoLlevar(datos.pedidoCreado.idPedido);
              if (pedidoServer) {
                pedidoActualizado = pedidoServer;
                console.log('🔄 Pedido refrescado desde servidor:', pedidoServer);
              }
            } catch (e) {
              console.error('Error al refrescar pedido desde servidor:', e);
            }
          }
          setPedidoExistente(pedidoActualizado);

          // Extraer nombre y teléfono del clienteLlevar (preferir datos del servidor)
          const clienteLlevar = pedidoActualizado.clienteLlevar || datos.pedidoCreado.clienteLlevar || datos.nombreCliente || '';
          console.log('📞 ClienteLlevar del servidor:', clienteLlevar);

          const partes = clienteLlevar.split(' - ');
          if (partes.length >= 2) {
            setClienteNombre(partes[0]);
            setClienteTelefono(partes[1]);
          } else {
            setClienteNombre(clienteLlevar);
            setClienteTelefono('');
          }
          setClienteCompleto(clienteLlevar);

          console.log('✅ Cliente configurado:', {
            nombre: partes[0] || clienteLlevar,
            telefono: partes[1] || '',
            completo: clienteLlevar
          });
        } else {
          // Modo crear nuevo, pero con datos de cliente
          setClienteNombre(datos.nombreCliente || '');
          setClienteTelefono(datos.telefonoCliente || '');
          setClienteCompleto(datos.nombreCliente ? `${datos.nombreCliente}${datos.telefonoCliente ? ' - ' + datos.telefonoCliente : ''}` : '');
        }
      } else {
        // Si no hay datos guardados, mostrar modal de cliente
        setTimeout(() => {
          setModalClienteVisible(true);
        }, 500);
      }

      inicializacionCompleta.current = true;
    } catch (err) {
      console.error("Error al cargar datos iniciales:", err);
      toast.error("Error al cargar datos iniciales del pedido");
    }
  }, [toast]);

  // ===== CONFIGURACIÓN PARA GESTIÓN PEDIDO BASE =====

  // Configuración específica para pedidos para llevar
  const configuracion = useMemo(() => ({
    permitirCambioMesa: false,           // No aplica para llevar
    permitirCambioMesero: false,         // No hay mesero en llevar
    permitirCambioPersonas: false,       // Siempre 1 persona para llevar
    permitirDatosCliente: true,          // SÍ necesita datos de cliente
    permitirDireccion: false,            // No necesita dirección (es para llevar)
    permitirRepartidor: false,           // No hay repartidor
    requiereImpresion: true,             // SÍ requiere impresión de comandas
    permitirPagoMultiple: true           // SÍ permite múltiples formas de pago
  }), []);

  // Datos del pedido para GestionPedidoBase
  const datosPedido = useMemo(() => {
    const datos = {
      idPedido: pedidoExistente?.idPedido || null,
      numeroPedido: numeroCuenta,
      // Usar el status real del pedido si existe, sino usar la lógica anterior
      status: pedidoExistente?.status || (modoEdicion ? 'abierto' : 'nuevo'),
      // Agregar pedidoExistente para que GestionPedidoBase pueda cargar los productos
      pedidoExistente: pedidoExistente,
      // Datos específicos de pedido para llevar
      clienteLlevar: clienteCompleto,
      nombreCliente: clienteNombre || (clienteCompleto ? clienteCompleto.split(' - ')[0] : null),
      telefono: clienteTelefono || (clienteCompleto && clienteCompleto.includes(' - ') ? clienteCompleto.split(' - ')[1] : null),
      numeroPersonas: 1, // Siempre 1 para llevar
      fechaCreacion: fechaHora,
      // Para compatibilidad con infoPedido en TomaPedidos
      numeroMesa: 'LLEVAR',
      nombreMesa: 'Para Llevar',
      mesa: {
        numero: 'LLEVAR',
        nombre: 'Para Llevar'
      },
      mesero: null // No se usa mesero en pedidos para llevar
    };

    return datos;
  }, [pedidoExistente, numeroCuenta, modoEdicion, clienteCompleto, clienteNombre, clienteTelefono, fechaHora]);

  // ===== CALLBACKS PARA GESTIÓN PEDIDO BASE =====

  const onPedidoCreado = useCallback((nuevoPedido) => {
    toast.success("Pedido para llevar creado correctamente");

    // Actualizar estados locales con los datos del pedido creado
    if (nuevoPedido.numeroDia || nuevoPedido.numero) {
      setNumeroCuenta(nuevoPedido.numeroDia || nuevoPedido.numero);
    }

    // IMPORTANTE: Actualizar pedidoExistente para que el useMemo tenga el idPedido
    if (nuevoPedido.idPedido) {
      setPedidoExistente(nuevoPedido);
      setModoEdicion(true); // Cambiar a modo edición ya que ahora existe el pedido

      // Actualizar el history state para que si navegamos a pago y regresamos,
      // location.state tenga los datos del pedido y no muestre el modal de cliente
      navigate(location.pathname, {
        replace: true,
        state: {
          datosPedido: {
            esEdicion: true,
            pedidoCreado: nuevoPedido,
            nombreCliente: clienteNombre,
            telefonoCliente: clienteTelefono
          }
        }
      });
    }
  }, [toast, navigate, location.pathname, clienteNombre, clienteTelefono]);

  const onPedidoActualizado = useCallback((pedidoActualizado) => {
    toast.success("Pedido para llevar actualizado correctamente");

    // Actualizar el estado local del pedido con los nuevos datos
    if (pedidoActualizado && pedidoExistente) {
      setPedidoExistente(prev => ({
        ...prev,
        ...pedidoActualizado
      }));
    }
  }, [toast, pedidoExistente]);

  const onPedidoCerrado = useCallback((resultado) => {
    toast.success("Pedido para llevar finalizado correctamente");

    // Actualizar el estado local del pedido con el nuevo status
    if (resultado && pedidoExistente) {
      const pedidoActualizado = {
        ...pedidoExistente,
        status: 'pendiente' // Pedidos para llevar se cambian a pendiente al finalizar
      };
      setPedidoExistente(pedidoActualizado);

      // Actualizar history state con el status actualizado
      navigate(location.pathname, {
        replace: true,
        state: {
          datosPedido: {
            esEdicion: true,
            pedidoCreado: pedidoActualizado,
            nombreCliente: clienteNombre,
            telefonoCliente: clienteTelefono
          }
        }
      });
    }
  }, [toast, pedidoExistente, navigate, location.pathname, clienteNombre, clienteTelefono]);

  // ===== MANEJADORES ESPECÍFICOS DEL MODAL DE CLIENTE =====

  const confirmarCliente = useCallback(async () => {
    if (!clienteNombreTemp.trim()) {
      toast.warning("El nombre del cliente es obligatorio");
      return;
    }

    const nombreLimpio = clienteNombreTemp.trim();
    const telefonoLimpio = clienteTelefonoTemp.trim();
    const clienteCompletoNuevo = telefonoLimpio ?
      `${nombreLimpio} - ${telefonoLimpio}` :
      nombreLimpio;

    // Si el pedido ya existe, actualizar en la BD
    if (pedidoExistente?.idPedido) {
      try {
        await API.pedidos.actualizarPedidoLlevar({
          idPedido: pedidoExistente.idPedido,
          clienteLlevar: clienteCompletoNuevo
        });
        toast.success(`Cliente actualizado: ${clienteCompletoNuevo}`);
      } catch (error) {
        console.error('Error al actualizar cliente:', error);
        toast.error('Error al actualizar los datos del cliente');
        return;
      }
    } else {
      toast.success(`Cliente: ${clienteCompletoNuevo}`);
    }

    setClienteNombre(nombreLimpio);
    setClienteTelefono(telefonoLimpio);
    setClienteCompleto(clienteCompletoNuevo);
    setModalClienteVisible(false);
  }, [clienteNombreTemp, clienteTelefonoTemp, pedidoExistente, toast]);

  const cancelarCliente = useCallback(() => {
    if (!clienteCompleto) {
      // Si no hay cliente configurado, regresar a la lista
      navigate('/ventas');
      return;
    }

    setModalClienteVisible(false);
    setClienteNombreTemp(clienteNombre);
    setClienteTelefonoTemp(clienteTelefono);
  }, [clienteCompleto, clienteNombre, clienteTelefono, navigate]);

  const editarCliente = useCallback(() => {
    setClienteNombreTemp(clienteNombre);
    setClienteTelefonoTemp(clienteTelefono);
    setModalClienteVisible(true);
  }, [clienteNombre, clienteTelefono]);

  // ===== VALIDACIÓN DE CLIENTE =====
  
  const validarCliente = useCallback(() => {
    if (!clienteCompleto) {
      toast.error("Debe proporcionar los datos del cliente");
      setModalClienteVisible(true);
      return false;
    }
    return true;
  }, [clienteCompleto, toast]);

  // ===== RENDER =====

  // Si no hay cliente configurado y no está mostrando el modal, mostrar carga
  if (!clienteCompleto && !modalClienteVisible) {
    return (
      <div className="toma-pedidos-cargando">
        <div className="spinner"></div>
        <p>Configurando pedido para llevar...</p>
      </div>
    );
  }

  return (
    <>
      {/* Usar GestionPedidoBase con configuración específica para llevar */}
      <GestionPedidoBase
        tipoPedido="llevar"
        datosPedido={datosPedido}
        configuracion={configuracion}
        onPedidoCreado={onPedidoCreado}
        onPedidoActualizado={onPedidoActualizado}
        onPedidoCerrado={onPedidoCerrado}
        rutaRegreso="/ventas"
        // Función de validación personalizada antes de ciertas acciones
        validacionesPersonalizadas={{
          antesDeGuardar: validarCliente,
          antesDeImprimir: validarCliente,
          antesDeFinalizar: validarCliente
        }}
        // Función para mostrar/editar cliente
        onEditarCliente={editarCliente}
      />

      {/* Modal de datos del cliente - específico para llevar */}
      {modalClienteVisible && (
        <div className="modal-overlay">
          <div className="modal-container modal-cliente">
            <div className="modal-header">
              <h2>Datos del Cliente</h2>
              <button
                className="close-button"
                onClick={cancelarCliente}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="nombreClienteInput">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  id="nombreClienteInput"
                  value={clienteNombreTemp}
                  onChange={(e) => setClienteNombreTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Ingrese el nombre del cliente"
                  autoFocus
                  maxLength="100"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telefonoClienteInput">
                  Teléfono (opcional)
                </label>
                <input
                  type="text"
                  id="telefonoClienteInput"
                  value={clienteTelefonoTemp}
                  onChange={(e) => setClienteTelefonoTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Ingrese el teléfono"
                  maxLength="15"
                />
              </div>

              <div className="cliente-preview">
                <strong>Se guardará como:</strong>
                <span>
                  {clienteNombreTemp.trim() ?
                    (clienteTelefonoTemp.trim() ?
                      `${clienteNombreTemp.trim()} - ${clienteTelefonoTemp.trim()}` :
                      clienteNombreTemp.trim()
                    ) :
                    'Ingrese al menos el nombre'
                  }
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="accept-button"
                onClick={confirmarCliente}
                disabled={!clienteNombreTemp.trim()}
              >
                ✓ Confirmar
              </button>
              <button
                className="cancel-button"
                onClick={cancelarCliente}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreacionPedidoLlevar;